import Big from 'big.js'

import type { RootInit } from '@/core/domains/root/entity/base'
import type { UserCurrencyCode } from '@/core/domains/user/entity/base'

import { currencyLocaleFormat, defaultCurrency } from '@/core/constants/currency'
interface ComposableCurrency {
  convertCurrencyToUsd: (price: number, currencyCode: string) => number
  convertCurrencyToUsdWithoutRounding: (price: number, currencyCode: string) => number
  convertCurrentCurrencyToUsd: (price: number, round?: boolean) => number
  convertCurrentCurrencyToUsdWithoutRounding: (price: number) => number
  convertUsdToCurrency: (price: number, currencyCode: string, digits?: number) => number
  convertUsdToCurrentCurrency: (price: number, digits?: number) => number
  convertUsdToCurrentCurrencyCeil: (price: number, digits?: number) => number
  currencyList: ComputedRef<Currency[]>
  currentCurrencyCode: ComputedRef<UserCurrencyCode>
  currentCurrencyDigit: ComputedRef<number>
  currentCurrencyFiat: ComputedRef<boolean>
  currentCurrencyName: ComputedRef<string>
  currentCurrencyRate: ComputedRef<number>
  currentCurrencySymbol: ComputedRef<string | undefined>
  formatCurrency: (price: number, noConvert?: boolean) => string
  formatPrettyCurrency: (options: FormatPrettyCurrencyOptions) => string
  formatUsdToCurrentCurrency: (price: number) => string
  getCurrencyByCode: (code: string) => Currency
  getCurrencyLabel: (code: string) => string
  getDenominatorByCode: (code: string) => number
  isCurrencyFiat: (code: string) => boolean
  setCurrentCurrencyByCode: (code: string) => void
  truncate: (num: number, digits: number) => number
}
type Currency = NestedPick<RootInit, ['config', 'currency']>[string]
type CurrencyInput = ComputedRef<string | undefined> | Ref<string | undefined> | string | undefined
type FormatPrettyCurrencyOptions = {
  code?: string
  convert?: boolean
  decimals?: number
  forceDecimals?: boolean
  hideSuffix?: boolean
  precision?: boolean
  price: number
  shortFrom?: number
  shortValue?: boolean
  sign?: '+' | '-'
  type?: 'code' | 'symbol' | 'text'
}

const htmlEntityMap: Record<string, string> = {
  amp: '&',
  cent: '¢',
  euro: '€',
  nbsp: ' ',
  pound: '£',
  yen: '¥',
}

export function useCurrency(payload?: { crypto?: boolean; currency?: CurrencyInput }): ComposableCurrency {
  const rootStore = useBrickRootStore()
  const userStore = useBrickUserStore()
  const { $di } = useNuxtApp()
  const payloadCurrency = computed(() => unref(payload?.currency))
  const currencyList = computed(() => {
    return Object.values(rootStore?.config?.currency ?? []).filter(
      (curr) => curr.active === true || payloadCurrency.value === curr.code,
    )
  })
  const currencyCodeMap = computed(() => {
    const map: Record<string, Currency> = {}
    currencyList.value.forEach((currency) => {
      map[currency.code] = currency
    })
    return map
  })
  const getCurrencyByCode = (code: string): Currency => rootStore.config?.currency?.[code] ?? defaultCurrency

  const setCurrentCurrencyByCode = (code: string): void => {
    $di.user.setCurrency(getCurrencyByCode(code))
  }

  const currentCurrencyFiat = computed((): boolean => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.fiat ?? true
    }
    return userStore?.currentCurrency?.fiat ?? true
  })
  const currentCurrencyCode = computed<UserCurrencyCode>(() => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.code ?? 'USD'
    }
    if (payload?.crypto === false && !currentCurrencyFiat.value) {
      return currencyCodeMap.value.USD?.code ?? 'USD'
    }
    return userStore?.currentCurrency?.code ?? 'USD'
  })
  const currentCurrencySymbol = computed<string | undefined>(() => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.symbol ?? '$'
    }
    if (payload?.crypto === false && !currentCurrencyFiat.value) {
      return currencyCodeMap.value.USD?.symbol ?? '$'
    }
    return currencyCodeMap.value[userStore?.currentCurrency?.code ?? 0]?.symbol ?? '$'
  })
  const currentCurrencyName = computed<string>(() => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.name ?? 'US Dollar'
    }
    if (payload?.crypto === false && !currentCurrencyFiat.value) {
      return currencyCodeMap.value.USD?.name ?? 'US Dollar'
    }

    return currencyCodeMap.value[userStore?.currentCurrency?.code ?? 0]?.name ?? 'US Dollar'
  })
  const currentCurrencyRate = computed((): number => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.rate ?? 1
    }
    if (payload?.crypto === false && !currentCurrencyFiat.value) {
      return currencyCodeMap.value.USD?.rate ?? 1
    }
    return userStore?.currentCurrency?.rate ?? 1
  })
  const currentCurrencyDigit = computed((): number => {
    if (payloadCurrency.value) {
      return currencyCodeMap.value[payloadCurrency.value]?.denominator ?? 2
    }
    if (payload?.crypto === false && !currentCurrencyFiat.value) {
      return currencyCodeMap.value.USD?.denominator ?? 2
    }
    return Number(userStore?.currentCurrency?.denominator) || 2
  })

  const getDenominatorByCode = (code: string): number => {
    return rootStore.config?.currency?.[code]?.denominator ?? 6
  }

  const isCurrencyFiat = (code: string): boolean => {
    const rawCurrency = currencyCodeMap.value[code]
    return rawCurrency ? rawCurrency.fiat : false
  }

  const truncate = (num: number, digits: number): number => {
    const [int, dec = ''] = num.toFixed(digits + 1).split('.')
    return Number(int + (digits > 0 ? `.${dec.slice(0, digits)}` : ''))
  }

  const convertUsdToCurrentCurrency = (price: number, digits?: number): number => {
    const bPrice = new Big(Number.isNaN(price) ? 0 : (price ?? 0))
    const convertedPrice = bPrice.times(currentCurrencyRate.value)
    return truncate(convertedPrice.toNumber(), digits ?? currentCurrencyDigit.value)
  }

  const convertUsdToCurrentCurrencyCeil = (price: number, digits?: number): number => {
    const bPrice = new Big(Number.isNaN(price) ? 0 : (price ?? 0))
    return bPrice
      .times(currentCurrencyRate.value)
      .round(digits ?? currentCurrencyDigit.value, Big.roundUp)
      .toNumber()
  }

  const formatUsdToCurrentCurrency = (price: number): string => {
    return convertUsdToCurrentCurrency(price).toFixed(currentCurrencyDigit.value)
  }

  const convertCurrentCurrencyToUsd = (price: number, round: boolean = true): number => {
    const convertedPrice = price / currentCurrencyRate.value
    return round ? truncate(convertedPrice, currentCurrencyDigit.value) : convertedPrice
  }

  const convertCurrentCurrencyToUsdWithoutRounding = (price: number): number => {
    const bPrice = new Big(Number.isNaN(price) ? 0 : (price ?? 0))
    return bPrice.div(currentCurrencyRate.value || 1).toNumber()
  }
  const convertCurrencyToUsdWithoutRounding = (price: number, currencyCode: string): number => {
    const currency = getCurrencyByCode(currencyCode)

    const bPrice = new Big(Number.isNaN(price) ? 0 : price)

    return bPrice.div(currency.rate || 1).toNumber()
  }

  const convertCurrencyToUsd = (price: number, currencyCode: string): number => {
    const currency = getCurrencyByCode(currencyCode)
    const convertedPrice = price / currency.rate
    return truncate(convertedPrice, currentCurrencyDigit.value)
  }

  const convertUsdToCurrency = (price: number, currencyCode: string, digits?: number): number => {
    const currency = getCurrencyByCode(currencyCode)

    const convertedPrice = price * currency.rate

    return truncate(convertedPrice, digits ?? currentCurrencyDigit.value)
  }

  const formatCurrency = (price: number, noConvert: boolean = false): string => {
    const convertedPrice = noConvert ? price : convertUsdToCurrentCurrency(price)
    return truncate(Number(convertedPrice), currentCurrencyDigit.value).toFixed(currentCurrencyDigit.value)
  }

  const formatPrettyCurrency = ({
    code,
    convert = true,
    decimals,
    forceDecimals,
    hideSuffix,
    precision,
    price,
    shortFrom,
    shortValue,
    sign,
    type = 'text',
  }: FormatPrettyCurrencyOptions): string => {
    const currency = getCurrencyByCode(code ?? currentCurrencyCode.value)
    const digits = code ? currency.denominator : currentCurrencyDigit.value
    const convertPrice = (convertDigits: number): number => {
      if (!convert) {
        return price
      }
      return code
        ? convertUsdToCurrency(price, code, convertDigits)
        : convertUsdToCurrentCurrency(price, convertDigits)
    }
    const currencyDigit = precision && convertPrice(digits + 1) < 10 ** -digits ? digits + 1 : digits
    const shortPrice = convertPrice(currencyDigit)
    const isShort = shortValue || (shortFrom !== undefined && Math.abs(shortPrice) >= shortFrom)
    const localeFormat = currencyLocaleFormat[currency.code]

    const fractionDigits = decimals ?? currencyDigit
    let formatted: string

    if (isShort) {
      formatted = Intl.NumberFormat('en-US', {
        maximumFractionDigits: currencyDigit,
        minimumFractionDigits: forceDecimals ? fractionDigits : 0,
        notation: 'compact',
      }).format(shortPrice)
    } else if (localeFormat) {
      formatted = Intl.NumberFormat(localeFormat.locale, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      }).format(convertPrice(fractionDigits))
    } else {
      formatted = Intl.NumberFormat('en-US', {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      })
        .format(convertPrice(fractionDigits))
        .replace(/,/g, ' ')
    }

    if (hideSuffix) {
      return `${sign ?? ''}${formatted}`
    }

    if (localeFormat?.prefix) {
      return `${sign ?? ''}${localeFormat.symbol}${localeFormat.space ? ' ' : ''}${formatted}`
    }

    const suffix = type === 'code' ? currency.code : getCurrencyLabel(currency.code)

    return `${sign ?? ''}${formatted} ${suffix}`
  }

  function getCurrencyLabel(code: string): string {
    const localeSymbol = currencyLocaleFormat[code.toUpperCase()]?.symbol
    if (localeSymbol) {
      return localeSymbol
    }
    const symbol = getCurrencyByCode(code).symbol
    if (!symbol || symbol.includes('<')) {
      return code
    }
    const decoded = decodeHtmlEntities(symbol).trim()
    return decoded && !/&[#a-z0-9]+;/i.test(decoded) ? decoded : code
  }

  return {
    convertCurrencyToUsd,
    convertCurrencyToUsdWithoutRounding,
    convertCurrentCurrencyToUsd,
    convertCurrentCurrencyToUsdWithoutRounding,
    convertUsdToCurrency,
    convertUsdToCurrentCurrency,
    convertUsdToCurrentCurrencyCeil,
    currencyList,
    currentCurrencyCode,
    currentCurrencyDigit,
    currentCurrencyFiat,
    currentCurrencyName,
    currentCurrencyRate,
    currentCurrencySymbol,
    formatCurrency,
    formatPrettyCurrency,
    formatUsdToCurrentCurrency,
    getCurrencyByCode,
    getCurrencyLabel,
    getDenominatorByCode,
    isCurrencyFiat,
    setCurrentCurrencyByCode,
    truncate,
  }
}

function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const isHex = entity[1]?.toLowerCase() === 'x'
      const codePoint = Number.parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10)
      return Number.isNaN(codePoint) ? match : String.fromCodePoint(codePoint)
    }
    return htmlEntityMap[entity.toLowerCase()] ?? match
  })
}
